import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper"
import KanbanTable from "@/common/components/KanbanTable"
import ListFilters from "@/common/components/ListFilters"
import ListHeader from "@/common/components/ListHeader"
import TextField from "@/common/components/TextField"
import useDebounce from "@/common/hooks/useDebounce"
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from "@/common/services/mapper"
import { UserData } from "@/common/types/users"
import { columnStateMapping } from "@/features/privateCompanyLeadsFunnel/constants/columnStateMapping.constant"
import { stateOptions } from "@/features/privateCompanyLeadsFunnel/constants/stateOptions.constant"
import usePrivateCompanyLeadsInfinite from "@/features/privateCompanyLeadsFunnel/hooks/usePrivateCompanyLeadsInfinite"
import { Building, Search } from "lucide-react"
import { useNavigate } from "react-router-dom"
import FilterCheckbox from "../../../../common/components/FilterCheckbox/"
import CreateModal from "../../components/CreateModal"
import { usePrivateCompanyLeadRelatedUsers } from "../../hooks/usePrivateCompanyLeadRelatedUsers"
import { PrivateCompanyLeadData } from "../../types/privateCompanyLead"

const List = () => {
  const navigate = useNavigate()
  const { filterParams, setFilterParams } = useFilterParams()
  const [searchLeadName, setSearchLeadName] = useDebounce(filterParams.leadName || '')
  const [searchCreator, setSearchCreator] = useDebounce('')

  const {
    data: registeredData = [],
    isPending: isPendingRegistered,
    hasNextPage: hasNextPageRegistered,
    isFetchingNextPage: isFetchingNextPageRegistered,
    fetchNextPage: fetchNextPageRegistered,
    error: registeredError = {} as any,
    refetch: refetchRegistered,
  } = usePrivateCompanyLeadsInfinite<PrivateCompanyLeadData[]>({
    filters: {
      search: searchLeadName || '',
      state: filterParams.state?.value ? [filterParams.state.value] : columnStateMapping.registered,
      creatorId: filterParams.creator?.value || '',
      showOnlyArchived: filterParams.archived === 'true',
    },
    options: {
      enabled: !filterParams.state?.value || columnStateMapping.registered.includes(filterParams.state.value),
    },
  })

  const {
    data: qualifyingData = [],
    isPending: isPendingQualifying,
    hasNextPage: hasNextPageQualifying,
    isFetchingNextPage: isFetchingNextPageQualifying,
    fetchNextPage: fetchNextPageQualifying,
    refetch: refetchQualifying,
  } = usePrivateCompanyLeadsInfinite<PrivateCompanyLeadData[]>({
    filters: {
      search: searchLeadName || '',
      state: filterParams.state?.value ? [filterParams.state.value] : columnStateMapping.qualifying,
      creatorId: filterParams.creator?.value || '',
      showOnlyArchived: filterParams.archived === 'true',
    },
    options: {
      enabled: !filterParams.state?.value || columnStateMapping.qualifying.includes(filterParams.state.value),
    },
  })

  const {
    data: quotingData = [],
    isPending: isPendingQuoting,
    hasNextPage: hasNextPageQuoting,
    isFetchingNextPage: isFetchingNextPageQuoting,
    fetchNextPage: fetchNextPageQuoting,
    refetch: refetchQuoting,
  } = usePrivateCompanyLeadsInfinite<PrivateCompanyLeadData[]>({
    filters: {
      search: searchLeadName || '',
      state: filterParams.state?.value ? [filterParams.state.value] : columnStateMapping.quoting,
      creatorId: filterParams.creator?.value || '',
      showOnlyArchived: filterParams.archived === 'true',
    },
    options: {
      enabled: !filterParams.state?.value || columnStateMapping.quoting.includes(filterParams.state.value),
    },
  })

  const {
    data: negotiatingData = [],
    isPending: isPendingNegotiating,
    hasNextPage: hasNextPageNegotiating,
    isFetchingNextPage: isFetchingNextPageNegotiating,
    fetchNextPage: fetchNextPageNegotiating,
    refetch: refetchNegotiating,
  } = usePrivateCompanyLeadsInfinite<PrivateCompanyLeadData[]>({
    filters: {
      search: searchLeadName || '',
      state: filterParams.state?.value ? [filterParams.state.value] : columnStateMapping.negotiating,
      creatorId: filterParams.creator?.value || '',
      showOnlyArchived: filterParams.archived === 'true',
    },
    options: {
      enabled: !filterParams.state?.value || columnStateMapping.negotiating.includes(filterParams.state.value),
    },
  })

  const {
    data: completedData = [],
    isPending: isPendingCompleted,
    hasNextPage: hasNextPageCompleted,
    isFetchingNextPage: isFetchingNextPageCompleted,
    fetchNextPage: fetchNextPageCompleted,
    refetch: refetchCompleted,
  } = usePrivateCompanyLeadsInfinite<PrivateCompanyLeadData[]>({
    filters: {
      search: searchLeadName || '',
      state: filterParams.state?.value ? [filterParams.state.value] : columnStateMapping.completed,
      creatorId: filterParams.creator?.value || '',
      showOnlyArchived: filterParams.archived === 'true',
    },
    options: {
      enabled: !filterParams.state?.value || columnStateMapping.completed.includes(filterParams.state.value),
    },
  })

  const { data: users } = usePrivateCompanyLeadRelatedUsers({
    filters: {
      search: searchCreator,
    },
    mapper: Mapper.mapToOptions<UserData>({ labelFieldName: 'full_name', valueFieldName: 'id' }),
  })

  const refetchAll = () => {
    refetchRegistered()
    refetchQualifying()
    refetchQuoting()
    refetchNegotiating()
    refetchCompleted()
  }

  return (
    <div className="flex flex-col gap-6">
      <ListHeader Icon={<Building className="w-10 h-10" strokeWidth={1.5} />} title="Funil de clientes privados" />

      <ErrorMessageWrapper
        className="h-[calc(100vh-128px)]"
        statusCode={registeredError?.status}
        refetchFunction={refetchAll}
      >
        <TextField
          LeftIcon={<Search />}
          placeholder="Pesquisar"
          className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
          value={filterParams.leadName || ''}
          onChange={(e: any) => {
            setSearchLeadName(e.target?.value)
            setFilterParams((prev) => ({
              ...prev,
              leadName: e.target?.value,
            }))
          }}
        />

        <div className="flex justify-between">
          <ListFilters
            filters={[
              {
                className: 'min-w-40',
                onChange: (e: any) => {
                  setFilterParams((prev) => ({
                    ...prev,
                    state: e.value ? e : undefined,
                  }))
                },
                value: filterParams.state || undefined,
                options: [
                  { label: 'Todos', value: '' },
                  ...stateOptions.filter((option) => option.value !== 'completed'),
                ],
                placeholder: 'Estado',
                dataTestid: 'state_filter',
              },
              {
                className: '',
                onInputChange: (inputValue) => setSearchCreator(inputValue),
                onChange: (e: any) => {
                  setFilterParams((prev) => ({
                    ...prev,
                    creator: e.value ? e : undefined,
                  }))
                },
                value: filterParams.creator || undefined,
                options: [{ label: 'Todos', value: '' }, ...(users || [])],
                placeholder: 'Responsável',
                dataTestid: 'creator_filter',
              },
            ]}
          />

          <FilterCheckbox
            isChecked={filterParams.archived === 'true'}
            onChange={(value) => {
              setFilterParams((prev) => ({
                ...prev,
                archived: value,
              }))
            }}
            label="Exibir apenas Arquivados"
            id="filter-checkbox"
            name="filter-checkbox"
          />
        </div>

        <div className="max-h-[calc(100vh-188px-4.5rem)]">
          <KanbanTable
            columns={[
              {
                title: 'Registrados',
                cards:
                  !filterParams.state?.value || columnStateMapping.registered.includes(filterParams.state.value)
                    ? registeredData
                    : [],
                headerButton: <CreateModal />,
                onClickOnCard: (id) => navigate(`/private-clients/${id}/edit`),
                isPending: isPendingRegistered,
                hasNextPage: hasNextPageRegistered,
                isFetchingNextPage: isFetchingNextPageRegistered,
                fetchNextPage: fetchNextPageRegistered,
                skeletonCount: 3,
                dataTestid: 'registered_column',
              },
              {
                title: 'Qualificando',
                cards:
                  !filterParams.state?.value || columnStateMapping.qualifying.includes(filterParams.state.value)
                    ? qualifyingData
                    : [],
                onClickOnCard: (id) => navigate(`/private-clients/${id}/edit`),
                isPending: isPendingQualifying,
                hasNextPage: hasNextPageQualifying,
                isFetchingNextPage: isFetchingNextPageQualifying,
                fetchNextPage: fetchNextPageQualifying,
                skeletonCount: 4,
                dataTestid: 'qualifying_column',
              },
              {
                title: 'Negociação',
                cards:
                  !filterParams.state?.value || columnStateMapping.quoting.includes(filterParams.state.value)
                    ? quotingData
                    : [],
                onClickOnCard: (id) => navigate(`/private-clients/${id}/edit`),
                isPending: isPendingQuoting,
                hasNextPage: hasNextPageQuoting,
                isFetchingNextPage: isFetchingNextPageQuoting,
                fetchNextPage: fetchNextPageQuoting,
                skeletonCount: 3,
                dataTestid: 'quoting_column',
              },
              {
                title: 'Contrato',
                cards:
                  !filterParams.state?.value || columnStateMapping.negotiating.includes(filterParams.state.value)
                    ? negotiatingData
                    : [],
                onClickOnCard: (id) => navigate(`/private-clients/${id}/edit`),
                isPending: isPendingNegotiating,
                hasNextPage: hasNextPageNegotiating,
                isFetchingNextPage: isFetchingNextPageNegotiating,
                fetchNextPage: fetchNextPageNegotiating,
                skeletonCount: 3,
                dataTestid: 'negotiating_column',
              },
              {
                title: 'Implantação',
                cards:
                  !filterParams.state?.value || columnStateMapping.completed.includes(filterParams.state.value)
                    ? completedData
                    : [],
                onClickOnCard: (id) => navigate(`/private-clients/${id}/edit`),
                isPending: isPendingCompleted,
                hasNextPage: hasNextPageCompleted,
                isFetchingNextPage: isFetchingNextPageCompleted,
                fetchNextPage: fetchNextPageCompleted,
                skeletonCount: 4,
                dataTestid: 'completed_column',
              },
            ]}
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  )
}

export default List
